import React, { useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getHeaderTitle, Schema, strings, translate, useSearchParams } from '../lib'
import { useCore, useLogin } from '../hooks'
import { Order } from 'depoto-core'
import { useHistory, useLocation } from 'react-router-dom'
import { Button } from './shadcn/Button'
import { InfiniteData, QueryObserver, useIsFetching, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { faArrowsRotate } from '@fortawesome/pro-duotone-svg-icons'
import { ProfileDropdown } from './ProfileDropdown'

export const Header: React.FC = () => {
  const { isLoggedIn } = useLogin()
  const { pathname } = useLocation()
  const [headerTitle, setHeaderTitle] = useState<string>(getHeaderTitle({ splitLocation: pathname.split('/') }))

  useEffect(() => {
    setHeaderTitle(translate(getHeaderTitle({ splitLocation: pathname.split('/') })))
  }, [pathname])

  const queryClient = useQueryClient()

  const isFetchingOrdersQuery = useIsFetching({ queryKey: ['orders'] })
  const isFetchingGroupsQuery = useIsFetching({ queryKey: ['orderGroups'] })

  const isFetchingOrders = isFetchingOrdersQuery > 0
  const isFetchingGroups = isFetchingGroupsQuery > 0

  const [ordersCount, setOrdersCount] = useState(0)
  const [groupCount, setGroupCount] = useState(0)

  const isGroups = pathname?.includes('groups')

  useEffect(() => {
    if (!isFetchingOrdersQuery && !isGroups) {
      const queries = queryClient.getQueriesData({ queryKey: ['orders'], stale: true })
      if (queries && Array.isArray(queries) && queries.length > 0) {
        const query = queries[queries.length - 1]
        const [_, queryData] = query as [
          unknown,
          InfiniteData<{ orders: { paginator: { totalCount: number } } }> | undefined,
        ]
        if (
          queryData &&
          queryData?.pages &&
          Array.isArray(queryData.pages) &&
          queryData.pages[0] &&
          queryData.pages[0].orders &&
          queryData.pages[0].orders.paginator
        ) {
          setOrdersCount(queryData.pages[0].orders.paginator.totalCount)
        }
      }
    }

    if (!isFetchingGroupsQuery && isGroups) {
      const queries = queryClient.getQueriesData({ queryKey: ['orderGroups'] })
      if (queries && Array.isArray(queries) && queries.length > 0) {
        const query = queries[queries.length - 1]
        const [_, queryData] = query as [
          unknown,
          InfiniteData<{ orderGroups: { paginator: { totalCount: number } } }> | undefined,
        ]
        if (
          queryData &&
          queryData?.pages &&
          Array.isArray(queryData.pages) &&
          queryData.pages[0] &&
          queryData.pages[0].orderGroups &&
          queryData.pages[0].orderGroups.paginator
        ) {
          setGroupCount(queryData.pages[0].orderGroups.paginator.totalCount)
        }
      }
    }
  }, [isFetchingOrdersQuery, isFetchingGroupsQuery])

  const isHistory = pathname?.includes('history')
  const isSettings = pathname?.includes('settings')
  const isDispatch =
    (pathname?.includes('dispatch') && !pathname?.includes('dashboard/dispatched')) || pathname?.includes('disposal')
  const isOrder = pathname?.includes('order')
  const isOrderGroup = pathname?.includes('group') && !pathname?.includes('groups')

  const currentFlow = useMemo(() => {
    if (isHistory) return 'history'
    if (isSettings) return 'settings'
    if (isDispatch) return 'dispatch'
    if (isGroups) return 'groups'
    return 'orders'
  }, [isHistory, isSettings, isDispatch, isGroups])

  const isNotListView = currentFlow !== 'orders' && currentFlow !== 'groups'

  if (isOrder || isOrderGroup) {
    return null
  }

  if (!isLoggedIn) {
    return null
  }

  return (
    <nav className="bg-white border-b border-cool-grey flex justify-between items-center min-h-16 px-3">
      <div className="flex flex-row divide-x divide-cool-grey">
        <div className="flex flex-row items-center text-center font-bold text-xl px-6">
          {isNotListView ? strings.appName : headerTitle}
        </div>
        {isHistory || isSettings ? null : (
          <div className="flex flex-row items-center">
            {currentFlow === 'orders' && (
              <div className="text-center font-bold text-dark-grey text-xl px-6">
                Celkem: {typeof ordersCount === 'number' && !isFetchingOrders ? ordersCount : '...'}
              </div>
            )}
            {currentFlow === 'groups' && (
              <div className="text-center font-bold text-dark-grey text-xl px-6">
                Celkem: {typeof groupCount === 'number' && !isFetchingGroups ? groupCount : '...'}
              </div>
            )}
            {currentFlow === 'orders' && (
              <FontAwesomeIcon
                icon={faArrowsRotate}
                className={`w-5 h-5 p-2 ${isFetchingGroups || isFetchingOrders ? 'animate-spin' : ''} cursor-pointer`}
                onClick={() => {
                  queryClient.refetchQueries({ queryKey: ['orders'], stale: true })
                }}
              />
            )}
            {currentFlow === 'groups' && (
              <FontAwesomeIcon
                icon={faArrowsRotate}
                className={`w-5 h-5 p-2 ${isFetchingGroups || isFetchingOrders ? 'animate-spin' : ''} cursor-pointer`}
                onClick={() => {
                  queryClient.refetchQueries({ queryKey: ['orderGroups'], stale: true })
                }}
              />
            )}
          </div>
        )}
        {isHistory ? <div className="text-center font-bold text-dark-grey text-xl px-6">Historie</div> : null}
        {isSettings ? <div className="text-center font-bold text-dark-grey text-xl px-6">Nastavení</div> : null}
        {isDispatch ? <div className="text-center font-bold text-dark-grey text-xl px-6">Svozy</div> : null}
      </div>

      <div className="relative flex items-center">
        <ProfileDropdown />
      </div>
    </nav>
  )
}
